import { useState } from 'react';
import { Button } from '@mui/material';
import {
  Confirm,
  useDataProvider,
  useNotify,
  useRefresh,
  type Identifier,
} from 'react-admin';
import { useAccessContext } from '../auth/useAccessContext';
import type { Capability } from '../auth/permissions';
import { performStatusTransition } from './performStatusTransition';

/**
 * A single status-transition action with a CONFIRMATION step (decision A). The
 * action fires only after the user confirms the dialog — never on first click.
 *
 * On confirm it calls `performStatusTransition` (the guarded chokepoint), which
 * checks the capability and writes the new status through the provider — audited
 * automatically by the #25 wrapper. There is no audit code here. On success it
 * refreshes so the record reflects the new status and the (now-illegal) action
 * buttons disappear.
 */
export interface TransitionButtonProps {
  /** The REAL underlying resource the status lives on (e.g. `cashoutRequests`). */
  resource: string;
  capability: Capability;
  id: Identifier;
  /** Single-field status transition (the common case). */
  fromStatus?: string;
  toStatus?: string;
  /** Multi-field patch (e.g. Subscriptions `{ plusStatus, autoRenew }`); passed
   *  straight through to the chokepoint, overriding the status convenience. */
  data?: Record<string, unknown>;
  previousData?: Record<string, unknown>;
  label: string;
  color?: 'primary' | 'success' | 'error' | 'warning' | 'inherit';
  confirmTitle: string;
  confirmContent: string;
  successMessage: string;
}

export const TransitionButton = ({
  resource,
  capability,
  id,
  fromStatus,
  toStatus,
  data,
  previousData,
  label,
  color = 'primary',
  confirmTitle,
  confirmContent,
  successMessage,
}: TransitionButtonProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { role } = useAccessContext();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const onConfirm = async () => {
    setLoading(true);
    try {
      await performStatusTransition({
        dataProvider,
        role,
        capability,
        resource,
        id,
        fromStatus,
        toStatus,
        data,
        previousData,
      });
      notify(successMessage, { type: 'success' });
      setOpen(false);
      setLoading(false);
      refresh();
    } catch {
      notify('Action not permitted or failed', { type: 'error' });
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button variant="contained" color={color} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Confirm
        isOpen={open}
        loading={loading}
        title={confirmTitle}
        content={confirmContent}
        confirm="Confirm"
        cancel="Cancel"
        onConfirm={onConfirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
